import { HookedRecordResult } from 'nuvo-react';
import { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { NUVO_DATE_REGEX, NUVO_DATE_FORMAT } from '../constants';

export function correctDateForNuvo(
  value: string,
  t: TFunction,
): HookedRecordResult {
  if (value) value = trimMilliseconds(value);

  const defaultValue = value.match(NUVO_DATE_REGEX)?.[0];
  if (defaultValue) {
    return {
      value: defaultValue,
    };
  }

  const correctValue = correctDate(value);
  if (correctValue) {
    return {
      value: correctValue,
      info: [
        {
          level: 'info',
          message: t('common.infos.correctedDateInfo', {
            defaultValue: 'Date is corrected to {{correctValue}}',
            correctValue,
          }),
        },
      ],
    };
  }

  return {
    value,
    info: [
      {
        level: 'error',
        message: t('common.errors.invalidDateFormatError', {
          defaultValue: 'Invalid date format',
        }),
      },
    ],
  };
}

const supportedDateFormats = [
  'dd.MM.yyyy',
  'dd.MM.yy',
  'd.M.yyyy',
  'd.M.yy',
  'yyyy.MM.dd',
  'yyyy.m.d',
]
  .map((format) => [format, format.replace('.', '-'), format.replace('.', '/')])
  .flat();

const supportedDateTimeFormats = [
  ...supportedDateFormats,
  ...supportedDateFormats
    .map((format) => [`${format} HH:mm`, `${format} HH:mm:ss`])
    .flat(),
];

function correctDate(value: string): string | null {
  try {
    for (const format of supportedDateTimeFormats) {
      const date = DateTime.fromFormat(value, format);
      if (date.isValid) {
        return date.toFormat(NUVO_DATE_FORMAT);
      }
    }

    let date = DateTime.fromSQL(value);
    if (date.isValid) {
      return date.toFormat(NUVO_DATE_FORMAT);
    }

    date = DateTime.fromISO(value);
    if (date.isValid) {
      return date.toFormat(NUVO_DATE_FORMAT);
    }

    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Trims milliseconds.
 *
 * e.g. 21.01.2024 06:00:00:000Z to 21.01.2024 06:00:00Z and
 *
 * 21.01.2024 06:00:00:000 to 21.01.2024 06:00:00
 *
 * @param value - string
 *
 **/
const trimMilliseconds = (value: string) => value.replace(/:\d{3}(?=\D|$)/, '');
